import { PROPERTY_STATUS_NAMES,PROPERTY_TYPE_NAMES, PropertyStatus, PropertyType } from "../types";
import { Button } from "@/components/ui/button";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { zodResolver } from "@hookform/resolvers/zod";
import { Filter,Search, X } from "lucide-react";
import { PropsWithChildren, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as z from "zod";

const propertyFiltersSchema = z.object({
    search: z.string().optional(),
    type: z.nativeEnum(PropertyType).optional(),
    status: z.nativeEnum(PropertyStatus).optional(),
    minPrice: z.string().optional().transform((val) => (val ? Number(val) : undefined)),
    maxPrice: z.string().optional().transform((val) => (val ? Number(val) : undefined)),
    city: z.string().optional(),
    minArea: z.string().optional().transform((val) => (val ? Number(val) : undefined)),
    maxArea: z.string().optional().transform((val) => (val ? Number(val) : undefined)),
    minRooms: z.string().optional().transform((val) => (val ? Number(val) : undefined)),
    maxRooms: z.string().optional().transform((val) => (val ? Number(val) : undefined)),
    sortBy: z.enum(['price', 'createdAt', 'area', 'rooms']).optional(),
    sortOrder: z.enum(['ASC', 'DESC']).optional(),
});

export type PropertyFiltersValues = z.infer<typeof propertyFiltersSchema>;

interface PropertyFiltersProps {
    onFiltersChange: (filters: PropertyFiltersValues) => void;
    defaultValues?: PropertyFiltersValues;
}

export const PropertyFilters = ({ onFiltersChange, defaultValues, children }: PropsWithChildren<PropertyFiltersProps>) => {
    const { t } = useTranslation();
    const [isFiltersVisible, setIsFiltersVisible] = useState(false);
    const form = useForm<PropertyFiltersValues>({
        resolver: zodResolver(propertyFiltersSchema),
        defaultValues: defaultValues || {
            search: "",
            type: undefined,
            status: undefined,
            minPrice: undefined,
            maxPrice: undefined,
            city: "",
            minArea: undefined,
            maxArea: undefined,
            minRooms: undefined,
            maxRooms: undefined,
            sortBy: undefined,
            sortOrder: undefined,
        },
    });

    const handleSubmit = (values: PropertyFiltersValues) => {
        onFiltersChange(values);
    };

    const handleReset = () => {
        form.reset({
            search: "",
            type: undefined,
            status: undefined,
            minPrice: undefined,
            maxPrice: undefined,
            city: "",
            minArea: undefined,
            maxArea: undefined,
            minRooms: undefined,
            maxRooms: undefined,
            sortBy: undefined,
            sortOrder: undefined,
        });
        onFiltersChange({});
    };

    return (
        <Form {...form}>
            <form
                onSubmit={form.handleSubmit(handleSubmit)}
                className="space-y-4 p-3 rounded-xl shadow-sm border"
            >
                {/* Пошук та кнопка фільтрів */}
                <div className="flex gap-4">
                    <div className="relative flex-1">
                        <FormField
                            control={form.control}
                            name="search"
                            render={({ field }) => (
                                <FormItem>
                                    <div className="relative">
                                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground/60" />
                                        <Input
                                            placeholder={t("properties.filters.searchPlaceholder")}
                                            className="pl-12 "
                                            {...field}
                                        />
                                    </div>
                                </FormItem>
                            )}
                        />
                    </div>
                    <Button
                        type="button"
                        variant={isFiltersVisible ? "secondary" : "outline"}
                        onClick={() => setIsFiltersVisible(!isFiltersVisible)}
                    >
                        <Filter className="h-4 w-4" />
                        {t("properties.filters.title")}
                    </Button>
                    {children}
                </div>

                {/* Розширені фільтри */}
                {isFiltersVisible && (
                    <div className="space-y-8">
                        {/* Основні фільтри */}
                        <div>
                            <h3 className="text-base font-semibold mb-4">{t("properties.filters.sections.mainFilters")}</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <FormField
                                    control={form.control}
                                    name="type"
                                    render={({ field }) => (
                                        <FormItem className="space-y-2">
                                            <FormLabel >{t("properties.filters.type")}</FormLabel>
                                            <Select
                                                onValueChange={field.onChange}
                                                value={field.value}
                                            >
                                                <FormControl>
                                                    <SelectTrigger >
                                                        <SelectValue placeholder={t("properties.filters.allTypes")} />
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent>
                                                    {Object.entries(PROPERTY_TYPE_NAMES).map(([key, value]) => (
                                                        <SelectItem key={key} value={key}>
                                                            {value}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="status"
                                    render={({ field }) => (
                                        <FormItem className="space-y-2">
                                            <FormLabel >{t("properties.filters.status")}</FormLabel>
                                            <Select
                                                onValueChange={field.onChange}
                                                value={field.value}
                                            >
                                                <FormControl>
                                                    <SelectTrigger >
                                                        <SelectValue placeholder={t("properties.filters.allStatuses")} />
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent>
                                                    {Object.entries(PROPERTY_STATUS_NAMES).map(([key, value]) => (
                                                        <SelectItem key={key} value={key}>
                                                            {value}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="city"
                                    render={({ field }) => (
                                        <FormItem className="space-y-2">
                                            <FormLabel >{t("properties.filters.city")}</FormLabel>
                                            <FormControl>
                                                <Input
                                                    placeholder={t("properties.filters.cityPlaceholder")}

                                                    {...field}
                                                />
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />
                            </div>
                        </div>

                        {/* Числові фільтри */}
                        <div>
                            <h3 className="text-base font-semibold mb-4">{t("properties.filters.sections.propertyParams")}</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {/* Ціна */}
                                <div className="space-y-2">
                                    <FormLabel >{t("properties.filters.price")}</FormLabel>
                                    <div className="flex gap-3">
                                        <FormField
                                            control={form.control}
                                            name="minPrice"
                                            render={({ field }) => (
                                                <FormItem className="flex-1">
                                                    <FormControl>
                                                        <Input
                                                            type="number"
                                                            placeholder={t("properties.filters.minPricePlaceholder")}

                                                            {...field}
                                                        />
                                                    </FormControl>
                                                </FormItem>
                                            )}
                                        />
                                        <FormField
                                            control={form.control}
                                            name="maxPrice"
                                            render={({ field }) => (
                                                <FormItem className="flex-1">
                                                    <FormControl>
                                                        <Input
                                                            type="number"
                                                            placeholder={t("properties.filters.maxPricePlaceholder")}

                                                            {...field}
                                                        />
                                                    </FormControl>
                                                </FormItem>
                                            )}
                                        />
                                    </div>
                                </div>

                                {/* Площа */}
                                <div className="space-y-2">
                                    <FormLabel >{t("properties.filters.area")}</FormLabel>
                                    <div className="flex gap-3">
                                        <FormField
                                            control={form.control}
                                            name="minArea"
                                            render={({ field }) => (
                                                <FormItem className="flex-1">
                                                    <FormControl>
                                                        <Input
                                                            type="number"
                                                            placeholder={t("properties.filters.minAreaPlaceholder")}

                                                            {...field}
                                                        />
                                                    </FormControl>
                                                </FormItem>
                                            )}
                                        />
                                        <FormField
                                            control={form.control}
                                            name="maxArea"
                                            render={({ field }) => (
                                                <FormItem className="flex-1">
                                                    <FormControl>
                                                        <Input
                                                            type="number"
                                                            placeholder={t("properties.filters.maxAreaPlaceholder")}

                                                            {...field}
                                                        />
                                                    </FormControl>
                                                </FormItem>
                                            )}
                                        />
                                    </div>
                                </div>

                                {/* Кімнати */}
                                <div className="space-y-2">
                                    <FormLabel >{t("properties.filters.rooms")}</FormLabel>
                                    <div className="flex gap-3">
                                        <FormField
                                            control={form.control}
                                            name="minRooms"
                                            render={({ field }) => (
                                                <FormItem className="flex-1">
                                                    <FormControl>
                                                        <Input
                                                            type="number"
                                                            placeholder={t("properties.filters.minRoomsPlaceholder")}

                                                            {...field}
                                                        />
                                                    </FormControl>
                                                </FormItem>
                                            )}
                                        />
                                        <FormField
                                            control={form.control}
                                            name="maxRooms"
                                            render={({ field }) => (
                                                <FormItem className="flex-1">
                                                    <FormControl>
                                                        <Input
                                                            type="number"
                                                            placeholder={t("properties.filters.maxRoomsPlaceholder")}

                                                            {...field}
                                                        />
                                                    </FormControl>
                                                </FormItem>
                                            )}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Сортування */}
                        <div>
                            <h3 className="text-base font-semibold mb-4">{t("properties.filters.sections.sorting")}</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <FormField
                                    control={form.control}
                                    name="sortBy"
                                    render={({ field }) => (
                                        <FormItem>
                                            <Select
                                                onValueChange={field.onChange}
                                                value={field.value}
                                            >
                                                <FormControl>
                                                    <SelectTrigger >
                                                        <SelectValue placeholder={t("properties.filters.sortByPlaceholder")} />
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent>
                                                    <SelectItem value="price">{t("properties.filters.sortByPrice")}</SelectItem>
                                                    <SelectItem value="createdAt">{t("properties.filters.sortByDate")}</SelectItem>
                                                    <SelectItem value="area">{t("properties.filters.sortByArea")}</SelectItem>
                                                    <SelectItem value="rooms">{t("properties.filters.sortByRooms")}</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="sortOrder"
                                    render={({ field }) => (
                                        <FormItem>
                                            <Select
                                                onValueChange={field.onChange}
                                                value={field.value}
                                            >
                                                <FormControl>
                                                    <SelectTrigger >
                                                        <SelectValue placeholder={t("properties.filters.sortOrderPlaceholder")} />
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent>
                                                    <SelectItem value="ASC">{t("properties.filters.sortOrderAsc")}</SelectItem>
                                                    <SelectItem value="DESC">{t("properties.filters.sortOrderDesc")}</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </FormItem>
                                    )}
                                />
                            </div>
                        </div>

                        {/* Кнопки */}
                        <div className="flex justify-end gap-4 mt-8 pt-6 border-t">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={handleReset}
                                className="h-11 px-6"
                            >
                                <X className="mr-2 h-4 w-4" />
                                {t("common.reset")}
                            </Button>
                            <Button
                                type="submit"
                                className="h-11 px-6"
                            >
                                <Search className="mr-2 h-4 w-4" />
                                {t("common.apply")}
                            </Button>
                        </div>
                    </div>
                )}
            </form>
        </Form>
    );
}; 